import type { Assinatura, AvisoDaAssinatura } from '@levitas/contracts';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@levitas/ui';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useSessao } from '../../lib/usar-sessao';

const CLASSE_POR_TOM: Record<AvisoDaAssinatura['tom'], string> = {
  informativo: 'bg-[var(--brand-suave)] text-[var(--brand-contraste)]',
  atencao: 'bg-[var(--status-aguardando-fundo)] text-[var(--cor-texto)]',
  bloqueio: 'bg-[var(--status-erro-fundo)] text-[var(--cor-texto)]',
};

/**
 * A faixa do topo (seção 11.2).
 *
 * Ela aparece quando há algo a dizer, e some quando não há — nunca em pop-up
 * agressivo, e nunca prometendo que os dados vão sumir, porque não vão. O
 * servidor decide o que dizer; a tela só escolhe a cor.
 *
 * Só quem cuida da conta enxerga: para quem serve, o assunto da igreja com a
 * El Shaday não é assunto dele.
 */
export function FaixaDaAssinatura() {
  const { pode } = useSessao();
  const podeVer = pode('billing:read');

  const { data } = useQuery({
    queryKey: ['assinatura'],
    queryFn: () => api<Assinatura>('/billing/subscription'),
    enabled: podeVer,
    // O estado muda em dias, não em segundos: reconsultar a cada navegação
    // seria uma consulta por tela sem nada de novo para mostrar.
    staleTime: 5 * 60 * 1000,
  });

  const aviso = data?.aviso;
  if (!aviso) return null;

  return (
    <div
      data-testid="faixa-da-assinatura"
      className={cn('px-4 py-2 text-center text-xs font-medium', CLASSE_POR_TOM[aviso.tom])}
    >
      <span className="font-semibold">{aviso.titulo}.</span> {aviso.detalhe}{' '}
      {aviso.acao ? (
        <Link to={aviso.acao.destino} className="underline underline-offset-2">
          {aviso.acao.rotulo}
        </Link>
      ) : null}
    </div>
  );
}
